/**
 * Persistent storage over localStorage, tolerant of browsers where it is missing, blocked or full.
 * Values are JSON; every key is namespaced and versioned by its owner.
 */

export interface KeyValueStore {
  get(key: string): string | null;
  set(key: string, value: string): void;
  remove(key: string): void;
}

class MemoryStore implements KeyValueStore {
  private readonly data = new Map<string, string>();
  get(key: string): string | null {
    return this.data.get(key) ?? null;
  }
  set(key: string, value: string): void {
    this.data.set(key, value);
  }
  remove(key: string): void {
    this.data.delete(key);
  }
}

class BrowserStore implements KeyValueStore {
  constructor(private readonly storage: Storage) {}
  get(key: string): string | null {
    try {
      return this.storage.getItem(key);
    } catch {
      return null;
    }
  }
  set(key: string, value: string): void {
    this.storage.setItem(key, value);
  }
  remove(key: string): void {
    try {
      this.storage.removeItem(key);
    } catch {
      // Ignored: nothing to remove when storage is unavailable.
    }
  }
}

function detect(): { store: KeyValueStore; persistent: boolean } {
  try {
    const storage = globalThis.localStorage;
    if (storage) {
      const probe = '__pawned_probe__';
      storage.setItem(probe, '1');
      storage.removeItem(probe);
      return { store: new BrowserStore(storage), persistent: true };
    }
  } catch {
    // Private mode or blocked storage: fall through to memory.
  }
  return { store: new MemoryStore(), persistent: false };
}

let active = detect();

/** Replaces the backing store (tests). */
export function useStore(store: KeyValueStore, persistent = true): void {
  active = { store, persistent };
}

export const storageIsPersistent = (): boolean => active.persistent;

/** The backing store in use (what it refused to keep belongs to it, not to the next one). */
export const currentStore = (): KeyValueStore => active.store;

export const PREFIX = 'pawned.';

export function readJson<T>(key: string, validate: (v: unknown) => T | null): T | null {
  const raw = active.store.get(PREFIX + key);
  if (raw === null) return null;
  try {
    return validate(JSON.parse(raw));
  } catch {
    return null;
  }
}

/** Writes a JSON value; returns an error message when the write fails (e.g. quota exceeded). */
export function writeJson(key: string, value: unknown): string | null {
  try {
    active.store.set(PREFIX + key, JSON.stringify(value));
    return null;
  } catch (e) {
    return e instanceof Error ? e.message : String(e);
  }
}

export function removeKey(key: string): void {
  active.store.remove(PREFIX + key);
}

export { MemoryStore };
